/**
 * The privacy page, as content without chrome (R22 T12).
 *
 * **Both frontends render this, and both must**: the App Store support URL and
 * the Play deletion-policy URL point at these pages, and a reviewer opens them.
 * Two copies of a compliance page is two chances for one of them to describe a
 * product Bookr does not ship, which is worse than having no page at all.
 *
 * Chrome is the host's: each frontend wraps this in its own header and footer.
 * Internal links take a `Link` component from the caller, so the static web app
 * keeps client-side navigation without this file importing a router.
 */
import type { StaticPageLink } from "./staticPage";

export function PrivacyContent({ Link }: { Link: StaticPageLink }) {
  return (
    <>

        <div className="grow bg-page px-4 py-12 text-body">
          <article className="mx-auto w-full max-w-2xl">
            <h1 className="font-display text-3xl font-bold text-ink sm:text-4xl">
              Privacy
            </h1>
            <p className="mt-3 text-lg">
              What Bookr knows about you, why it knows it, and what you can do
              about it.
            </p>
            <p className="mt-2 text-sm text-muted">Last updated 12 August 2026</p>

            <Section title="What Bookr is">
              <p>
                Bookr connects people in Lebanon with clinics and independent
                practitioners. You search, you request an appointment, and the
                clinic confirms it. Bookr is not a medical provider and holds no
                clinical records: it knows that you asked for an appointment, not
                what it was for beyond the name of the service you chose.
              </p>
            </Section>

            <Section title="What we collect, and why">
              <p>
                <strong className="text-ink">Your name and phone number.</strong>{" "}
                You give these when you create an account. The phone number is how
                you sign in and how a clinic identifies you when you arrive; it is
                verified with a one-time code because an unverified number would
                let anyone book in your name.
              </p>
              <p>
                <strong className="text-ink">Your appointments.</strong> Which
                business, which practitioner, which service, when, and whether you
                chose an in-clinic or a home visit. A home visit also stores the
                address you gave, because the practitioner has to be able to get
                there.
              </p>
              <p>
                <strong className="text-ink">Your reviews.</strong> A rating, and
                the text if you wrote any. These are public.
              </p>
              <p>
                <strong className="text-ink">
                  Your location, only if you ask for it.
                </strong>{" "}
                Your device location is used to sort clinics by distance. On the
                website it is asked for when you use &ldquo;Near me&rdquo;; in the
                apps, Find care asks for it the first time it opens, so it can
                list the nearest clinics first. It is used for that search and not
                stored, and declining it simply lists every clinic without
                distances.
              </p>
            </Section>

            <Section title="What we do not collect">
              <p>
                No payment details: Bookr takes no payment, and money changes hands
                between you and the clinic directly. No advertising identifiers, no
                third-party analytics, no tracking across other apps or websites.
                Nothing is sold to anybody, at any price, ever.
              </p>
            </Section>

            <Section title="Who sees what">
              <p>
                <strong className="text-ink">The clinic you book with</strong> sees
                your name, your phone number, the appointment, and the delivery
                address if you asked for a home visit. It needs all four to see
                you. It does not see your other bookings, with them or with anyone
                else.
              </p>
              <p>
                <strong className="text-ink">A clinic can also write notes about
                you</strong>, the way any clinic keeps its own file on the people
                it treats: what you told them, what they noticed, what to remember
                next time. Everyone who works at that clinic can read them. Nobody
                at any other clinic can, and they are never shown to you in the
                app. If you want to know what your clinic has written about you,
                ask the clinic: it is their record, they are the ones who can
                explain it, and we do not stand between you and them.
              </p>
              <p>
                <strong className="text-ink">A nutrition clinic can give you a
                meal plan</strong>. You see it in the app once the clinic shares
                it with you, and never while it is still being written. Everyone
                who works at that clinic can read it too, because they wrote it
                for you. Nobody at any other clinic can. The clinic keeps it for
                as long as the clinic is on Bookr.
              </p>
              <p>
                <strong className="text-ink">Other people</strong> see your reviews
                and the name attached to them. Nothing else about you is public.
              </p>
              <p>
                <strong className="text-ink">Bookr staff</strong> can see
                registrations awaiting approval and platform totals. Reading an
                individual&rsquo;s appointments is not part of running the
                platform and is not a thing the tools do.
              </p>
            </Section>

            <Section title="Notifications">
              <p>
                Bookr tells you what happened to your bookings in the app&rsquo;s
                own inbox. It does not email you, and it does not send you
                marketing of any kind. The one-time code you receive when signing
                up arrives over WhatsApp, because that is the channel that works
                reliably in Lebanon.
              </p>
            </Section>

            <Section title="How long we keep it">
              <p>
                Your account and its appointment history stay for as long as you
                have an account. A clinic keeps its own record of appointments you
                had with them, which is theirs and which they need in the same way
                any business needs a record of who it saw.
              </p>
              <p>
                <strong className="text-ink">Notes a clinic wrote about you are
                kept for as long as that clinic exists on Bookr</strong>, and go
                when it does. There is no expiry on them, and we would rather say
                that plainly than leave you to work it out.
              </p>
            </Section>

            <Section title="Your choices">
              <p>
                You can change your name and your password in the app at any time.
                Your phone number is fixed once verified, because changing it
                would mean re-verifying an identity a clinic may already have
                relied on; contact us and we will do it with you.
              </p>
              <p>
                <strong className="text-ink">You can delete your account
                yourself</strong>, in the app or on your{" "}
                <Link className="text-accent underline" href="/profile">
                  profile page
                </Link>
                . It happens immediately, with no waiting period, and it asks for
                your password so that somebody else holding your phone cannot do
                it for you.
              </p>
              <p>
                <strong className="text-ink">What is deleted:</strong> your name,
                your phone number, your password, every notification in your
                inbox, and any address you gave for a home visit. Upcoming
                appointments are cancelled.
              </p>
              <p>
                <strong className="text-ink">What a clinic keeps:</strong>{" "}
                appointments you actually had, with your name removed. A business
                needs its own record of who it saw, in the same way any business
                does. If you left a review, the star rating stays and the words go:
                ratings stay so that a business&rsquo;s public average cannot be
                changed after the fact by somebody closing their account.{" "}
                <strong className="text-ink">Notes a clinic wrote about you also
                stay</strong>, no longer linked to your account. We are saying that
                in as many words because a note can mention you by name, and
                &ldquo;we deleted your account&rdquo; should not be read as
                &ldquo;you are gone from Bookr&rdquo;. Those notes are the
                clinic&rsquo;s record of treating you, like the appointment
                itself; if you want them removed, ask the clinic.{" "}
                <strong className="text-ink">Meal plans a clinic gave you stay
                in the same way</strong>, no longer linked to your account: they
                are the clinic&rsquo;s record of what it advised.
              </p>
              <p>
                You are told all of this on the screen, before you confirm, not
                afterwards.
              </p>
            </Section>

            <Section title="Security">
              <p>
                Your password is stored hashed, never in a form anyone can read.
                On the mobile apps your session is kept in the phone&rsquo;s own
                secure store: the Keychain on an iPhone, and on Android a store
                encrypted under a key that never leaves the device. Either way it
                is tied to that phone and is not carried to a new one by a backup.
                Everything the apps send travels over HTTPS. Changing your
                password requires your current one, so somebody holding a session
                cannot quietly lock you out of your own account.
              </p>
            </Section>

            <Section title="Children">
              <p>
                Bookr is for adults. If you are booking care for a child, the
                account is yours and the appointment is made in your name.
              </p>
            </Section>

            <Section title="Getting in touch">
              <p>
                Write to us at{" "}
                <a className="text-accent underline" href="mailto:privacy@bookr.lb">
                  privacy@bookr.lb
                </a>
                . Ask us what we hold about you and we will tell you; ask us to
                correct it and we will.
              </p>
              <p className="text-sm text-muted">
                See also our <Link className="text-accent underline" href="/support">support page</Link>.
              </p>
            </Section>
          </article>
        </div>
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-10">
      <h2 className="font-display text-xl font-bold text-ink">{title}</h2>
      <div className="mt-3 space-y-3 leading-relaxed">{children}</div>
    </section>
  );
}
